import React from 'react'
import { useTheme } from '@emotion/react'
import { formatDateYYYYMMDD } from '../../packages/utils/numberFormatters'
import * as datefns from 'date-fns'
import { IssueEntity } from 'types/IssueEntity'
import { isValidSourceType } from '../../utils/issues/sourceTypes'
import { SourceType } from 'types/SourceType'
import Stack from '@mui/system/Stack'
import Typography from '@mui/material/Typography'

function hasSourceOfType(issue: IssueEntity, type: SourceType) {
    if (!isValidSourceType(type)) {
        throw new Error(`Invalid source type: ${type}`)
    }
    return issue.sources.some((source) => source.sourceType === type)
}

function useStatusMessage(issue: IssueEntity) {
    const theme = useTheme()
    if (!issue) return null

    const datePart = (date: string | null | Date) => (date ? datefns.startOfDay(new Date(date)) : null)

    const today = datePart(new Date()) as Date

    const {
        type,
        statusStage,
        statusComplete,
        noTermsInitially,
        rightsEndDate,
        unitIsTrading,
        subEndDate,
        subStartDate,
    } = issue

    const rightsEndDateFormatted = datePart(rightsEndDate)
    const subEndDateFormatted = datePart(subEndDate)
    const subStartDateFormatted = datePart(subStartDate)

    if (hasSourceOfType(issue, 'Cancellation')) {
        return { color: theme.palette.entityStatus.cancelled, text: 'Avbruten' }
    }

    if (type === 'Rights issue') {
        if (noTermsInitially && !hasSourceOfType(issue, 'Terms')) {
            return { color: theme.palette.entityStatus.incomplete, text: 'Kommande, villkor saknas' }
        }

        if (hasSourceOfType(issue, 'Outcome') && !unitIsTrading) {
            return { color: theme.palette.entityStatus.complete, text: 'Genomförd' }
        }

        if (subEndDateFormatted && datefns.isAfter(today, subEndDateFormatted)) {
            if (unitIsTrading) {
                return { color: theme.palette.entityStatus.complete, text: 'Handel i BTA/BTU pågår' }
            }
            return { color: theme.palette.entityStatus.complete, text: 'Teckningsperiod avslutad' }
        }

        if (
            rightsEndDateFormatted &&
            subStartDateFormatted &&
            (datefns.isAfter(today, subStartDateFormatted) || datefns.isSameDay(today, subStartDateFormatted))
        ) {
            return { color: theme.palette.entityStatus.ongoing, text: 'Teckningsperioden pågår' }
        }

        return { color: theme.palette.entityStatus.incomplete, text: 'Kommande' }
    }

    if (['Directed issue', 'Non-cash issue', 'ABB Directed issue'].includes(type)) {
        if (statusStage === 10 && !!statusComplete) {
            return { color: theme.palette.entityStatus.complete, text: 'Genomförd' }
        }

        if (type === 'ABB Directed issue') {
            return { color: theme.palette.entityStatus.ongoing, text: 'Pågående' }
        }

        return { color: theme.palette.entityStatus.incomplete, text: 'Aviserad' }
    }

    if (type === 'IPO') {
        if (subStartDateFormatted && datefns.isBefore(today, subStartDateFormatted)) {
            return { color: theme.palette.entityStatus.incomplete, text: 'Aviserad' }
        }

        if (
            subEndDateFormatted &&
            subStartDateFormatted &&
            (datefns.isAfter(today, subStartDateFormatted) || datefns.isSameDay(today, subStartDateFormatted)) &&
            (datefns.isBefore(today, subEndDateFormatted) || datefns.isSameDay(today, subEndDateFormatted))
        ) {
            return { color: theme.palette.entityStatus.ongoing, text: 'Teckningsperioden pågår' }
        }

        if (
            subEndDateFormatted &&
            (datefns.isAfter(today, subEndDateFormatted) || datefns.isSameDay(today, subEndDateFormatted)) &&
            issue.unitSubTotal === null &&
            issue.secondarySub === null
        ) {
            return { color: theme.palette.entityStatus.complete, text: 'Teckningsperiod avslutad' }
        }

        return { color: theme.palette.entityStatus.complete, text: 'Genomförd' }
    }

    return null
}
export function statusMessageWithExtraInfo(issue: IssueEntity) {
    if (!issue) return null

    const datePart = (date: string | null | Date) => (date ? datefns.startOfDay(new Date(date)) : null)

    const today = datePart(new Date()) as Date

    const {
        type,
        statusStage,
        statusComplete,
        noTermsInitially,
        incRightsDate,
        rightsEndDate,
        unitIsTrading,
        subEndDate,
        subStartDate,
        publicationDate,
        generalMeetingDate,
    } = issue

    const incRightsDateFormatted = datePart(incRightsDate)
    const rightsEndDateFormatted = datePart(rightsEndDate)
    const subEndDateFormatted = datePart(subEndDate)
    const subStartDateFormatted = datePart(subStartDate)
    const publicationDateFormatted = datePart(publicationDate)
    const generalMeetingDateFormatted = datePart(generalMeetingDate)

    if (hasSourceOfType(issue, 'Cancellation')) {
        return 'Avbruten'
    }

    if (type === 'Rights issue') {
        if (noTermsInitially && !hasSourceOfType(issue, 'Terms')) {
            return 'Kommande emission. Aviserad utan villkor'
        }

        if (hasSourceOfType(issue, 'Outcome') && !unitIsTrading) {
            return `Genomförd`
        }

        if (subEndDateFormatted && datefns.isAfter(today, subEndDateFormatted)) {
            let str =
                'Teckningsperiod avslutad' +
                (hasSourceOfType(issue, 'Outcome') ? ' och utfall har offentliggjorts' : '')
            if (unitIsTrading) {
                str += '. Handel i BTA/BTU pågår'
            }
            return str
        }

        if (
            rightsEndDateFormatted &&
            subStartDateFormatted &&
            (datefns.isAfter(today, subStartDateFormatted) || datefns.isSameDay(today, subStartDateFormatted))
        ) {
            return `Teckningsperioden pågår. Sista dagen för handel i rätter är ${formatDateYYYYMMDD(rightsEndDateFormatted)}`
        }

        if (incRightsDateFormatted && datefns.isAfter(today, incRightsDateFormatted)) {
            return `Offentliggjord emission. Sista dagen för att erhålla rätter var ${formatDateYYYYMMDD(
                incRightsDateFormatted,
            )}`
        }

        if (
            incRightsDateFormatted &&
            (datefns.isBefore(today, incRightsDateFormatted) || datefns.isSameDay(today, incRightsDateFormatted))
        ) {
            return `Kommande emission. Sista dagen för att erhålla rätter är ${formatDateYYYYMMDD(incRightsDateFormatted)}`
        }

        return 'Kommande emission. Datum saknas.'
    }

    if (['Directed issue', 'Non-cash issue', 'ABB Directed issue'].includes(type)) {
        if (statusStage === 10 && !!statusComplete) {
            return `Genomförd: ${formatDateYYYYMMDD(publicationDateFormatted)}`
        }

        if (type === 'ABB Directed issue') {
            return 'Pågående'
        }

        if (generalMeetingDateFormatted && datefns.isBefore(today, generalMeetingDateFormatted)) {
            return `Aviserad emission, bolagsstämma ${formatDateYYYYMMDD(generalMeetingDateFormatted)}`
        }

        return `Aviserad emission`
    }

    if (type === 'IPO') {
        if (subStartDateFormatted && datefns.isBefore(today, subStartDateFormatted)) {
            return 'Aviserad'
        }

        if (
            subEndDateFormatted &&
            subStartDateFormatted &&
            (datefns.isAfter(today, subStartDateFormatted) || datefns.isSameDay(today, subStartDateFormatted)) &&
            (datefns.isBefore(today, subEndDateFormatted) || datefns.isSameDay(today, subEndDateFormatted))
        ) {
            return 'Teckningsperiod pågår'
        }

        if (
            subEndDateFormatted &&
            (datefns.isAfter(today, subEndDateFormatted) || datefns.isSameDay(today, subEndDateFormatted)) &&
            issue.unitSubTotal === null &&
            issue.secondarySub === null
        ) {
            return 'Teckningsperiod avslutad'
        }

        return 'Genomförd'
    }

    return null
}

export const IssuePublicStatusWithExtraInfo = ({ issue }: { issue: IssueEntity }) => {
    const message = statusMessageWithExtraInfo(issue)
    return message ? <>Status: {message}</> : null
}

export const IssuePublicStatus = ({ issue }: { issue: IssueEntity }) => {
    const status = useStatusMessage(issue)

    if (!status) return null

    return (
        <Stack
            direction='row'
            spacing={1}
            sx={{
                alignItems: 'center',
            }}
        >
            <div
                style={{
                    height: '10px',
                    width: '10px',
                    margin: 0,
                    backgroundColor: status.color,
                    borderRadius: '50%',
                }}
            />
            <Typography>{status.text}</Typography>
        </Stack>
    )
}
